/**
 * The "Notify me when available" button, shown in place of the add to cart button on sold-out variants (PDP, sticky
 * bar, and quick-buy modal - see snippets/notify-me-form.liquid). Subscribes the visitor to a Klaviyo back in stock
 * alert directly from the browser via Klaviyo's public Client API, so no backend/app-proxy is required.
 */

class NotifyMeForm extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const emailInput = this.querySelector('input[name="notify_me_email"]');

    this.querySelector('[data-notify-me-submit]')?.addEventListener(
      'click',
      this._onSubmit.bind(this),
      { signal: this._abortController.signal }
    );

    emailInput?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this._onSubmit(event);
        }
      },
      { signal: this._abortController.signal }
    );
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  async _onSubmit(event) {
    if (event.currentTarget?.getAttribute('aria-busy') === 'true') {
      return;
    }

    const emailInput = this.querySelector('input[name="notify_me_email"]');

    if (!emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    const submitButton = this.querySelector('[data-notify-me-submit]');
    const messageElement = this.querySelector('[data-notify-me-message]');

    submitButton?.setAttribute('aria-busy', 'true');
    messageElement.hidden = true;
    messageElement.classList.remove('text-success', 'text-error');

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.getAttribute('public-api-key')}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: emailInput.value },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.getAttribute('variant-id')}`,
                  },
                },
              },
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Klaviyo back in stock subscription failed with status ${response.status}`);
      }

      this.querySelector('[data-notify-me-fields]').hidden = true;
      messageElement.textContent = window.themeVariables.strings.notifyMeSuccess;
      messageElement.classList.add('text-success');
    } catch (error) {
      messageElement.textContent = window.themeVariables.strings.notifyMeError;
      messageElement.classList.add('text-error');
    } finally {
      submitButton?.removeAttribute('aria-busy');
      messageElement.hidden = false;
    }
  }
}

if (!window.customElements.get('notify-me-form')) {
  window.customElements.define('notify-me-form', NotifyMeForm);
}
